import { FunctionComponent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CircularProgress from '@mui/material/CircularProgress';
import { userService } from '../../../api';
import { useApiCall } from '../../../hooks/useApiCall';
import styles from './ProfileContactInformationScreen.module.css';

interface ContactInfo {
  personalEmail: string;
  phoneNumber: string;
  workEmail?: string;
}

interface ValidationErrors {
  personalEmail?: string;
  phoneNumber?: string;
  workEmail?: string;
}

const ProfileContactInformationScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    personalEmail: '',
    phoneNumber: '',
    workEmail: ''
  });
  const [originalInfo, setOriginalInfo] = useState<ContactInfo>({
    personalEmail: '',
    phoneNumber: '',
    workEmail: ''
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  const { loading: isLoading, execute: fetchContactInfoApi } = useApiCall(userService.getContactInfo);
  const { loading: isSaving, execute: updateContactInfoApi } = useApiCall(userService.updateContactInfo);

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    const result = await fetchContactInfoApi();
    
    if (result?.success && result.data) {
      const info = {
        personalEmail: result.data.personalEmail || '',
        phoneNumber: result.data.phoneNumber || '',
        workEmail: result.data.workEmail || ''
      };
      setContactInfo(info);
      setOriginalInfo(info);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    // Basic phone validation - can be enhanced based on requirements
    const phoneRegex = /^\+?[\d\s-()]+$/;
    return phone.length >= 10 && phoneRegex.test(phone);
  };

  const handleInputChange = (field: keyof ContactInfo, value: string) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    // Clear success message when making changes
    setSaveSuccess(false);
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    
    if (!contactInfo.personalEmail) {
      newErrors.personalEmail = 'Personal email is required';
    } else if (!validateEmail(contactInfo.personalEmail)) {
      newErrors.personalEmail = 'Please enter a valid email address';
    }
    
    if (!contactInfo.phoneNumber) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!validatePhone(contactInfo.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    
    if (contactInfo.workEmail && !validateEmail(contactInfo.workEmail)) {
      newErrors.workEmail = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    setSaveSuccess(false);
    
    const result = await updateContactInfoApi(contactInfo);
    
    if (result?.success) {
      setOriginalInfo(contactInfo);
      setSaveSuccess(true);
      // Show success for 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  const hasChanges = () => {
    return JSON.stringify(contactInfo) !== JSON.stringify(originalInfo);
  };

  if (isLoading) {
    return (
      <div className={styles.profileContactInformation}>
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileContactInformation}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/profile')}>
          <ArrowBackIcon />
        </button>
        <h1 className={styles.headerTitle}>Contact Information</h1>
        <div className={styles.headerSpacer} />
      </div>

      <div className={styles.content}>
        <div className={styles.formSection}>
          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
              <EmailIcon className={styles.labelIcon} />
              Personal Email
            </label>
            <input
              type="email"
              className={`${styles.input} ${errors.personalEmail ? styles.inputError : ''}`}
              value={contactInfo.personalEmail}
              onChange={(e) => handleInputChange('personalEmail', e.target.value)}
              placeholder="Enter your personal email"
            />
            {errors.personalEmail && (
              <span className={styles.errorMessage}>
                <ErrorIcon className={styles.errorIcon} />
                {errors.personalEmail}
              </span>
            )}
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
              <PhoneIcon className={styles.labelIcon} />
              Phone Number
            </label>
            <input
              type="tel"
              className={`${styles.input} ${errors.phoneNumber ? styles.inputError : ''}`}
              value={contactInfo.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              placeholder="Enter your phone number"
            />
            {errors.phoneNumber && (
              <span className={styles.errorMessage}>
                <ErrorIcon className={styles.errorIcon} />
                {errors.phoneNumber}
              </span>
            )}
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.inputLabel}>
              <BusinessIcon className={styles.labelIcon} />
              Work Email (Optional)
            </label>
            <input
              type="email"
              className={`${styles.input} ${errors.workEmail ? styles.inputError : ''}`}
              value={contactInfo.workEmail}
              onChange={(e) => handleInputChange('workEmail', e.target.value)}
              placeholder="Enter your work email"
            />
            {errors.workEmail && (
              <span className={styles.errorMessage}>
                <ErrorIcon className={styles.errorIcon} />
                {errors.workEmail}
              </span>
            )}
          </div>
        </div>

        {saveSuccess && (
          <div className={styles.successMessage}>
            <CheckCircleIcon className={styles.successIcon} />
            Contact information updated successfully!
          </div>
        )}

        <div className={styles.actionSection}>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={!hasChanges() || isSaving}
          >
            {isSaving ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <span>Saving...</span>
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>

        <div className={styles.infoSection}>
          <h3 className={styles.infoTitle}>How we use your information</h3>
          <p className={styles.infoText}>
            Your personal email is used for account-related communications and important updates.
          </p>
          <p className={styles.infoText}>
            Your phone number may be used for appointment reminders and urgent health notifications.
          </p>
          <p className={styles.infoText}>
            Your work email, if provided, can be used for employer-sponsored benefits information.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileContactInformationScreen;